import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Button,
  Paper,
  Snackbar,
  Alert,
  Stack,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  Divider,
} from "@mui/material";
import { AddCircleOutline, Delete } from "@mui/icons-material";
import moment from "moment";
import {
  MonthlyHolidayData,
  MonthlyTourProgramData,
} from "../../../../../domain/models/monthly-tour-program/fetch-monthly-tour-program";
import AddTourProgramFormModalMobile from "../Modal/AddTourProgramFormModalMobile";
import SmartFillModalMobile from "../Modal/SmartFillModalMobile";
import ConfirmDeleteModalMobile from "../Modal/ConfirmDeleteModalMobile";
import { MonthlyTourProgramStore } from "../../../../../store/main/MonthlyTourProgramStore";
import { CustomerDatabaseStore } from "../../../../../store/main/CustomerDatabaseStore";
import { MonthlySmartFillHolidayData } from "../../../../../domain/models/monthly-tour-program/fetch-monthly-smart-fill";
import IMAGES from "../../../../../assets/images/images";
import AddTourProgramFormModal from "../../../desktop/monthly-tour-program/Modal/AddTourProgramFormModal";
import Swal from "sweetalert2";
import { tourType } from "../../../../../common/constants/tourProgramFilters";

interface TourProgramCardViewMobileProps {
  month: number;
  year: number;
  data: MonthlyTourProgramData[];
  holiday: MonthlyHolidayData[];
  editable: boolean;
  specialAccessDays: string[];
  filterType?:
    | "All"
    | "Approved"
    | "Rejected"
    | "Approval Pending"
    | "Tour Not Set";
  refreshData?: () => void;
}

type SmartFillFlatEntry = {
  type: string;
  working_with_id: number | null;
  hq_brick_id: number | null;
  hq_territory_id: number | null;
  hq_headquarter_id: number | null;
};

// ✅ Represents all date-wise suggestions (key = date string)
type SmartFillSuggestions = {
  [date: string]: SmartFillFlatEntry[];
};

type SmartFillSuggestionsData = {
  success: boolean;
  dominant_month: number;
  suggestions: SmartFillSuggestions;
};

const TourProgramCardViewMobile = ({
  month,
  year,
  data,
  holiday = [],
  editable,
  filterType,
  refreshData,
  specialAccessDays,
}: TourProgramCardViewMobileProps) => {
  const daysInMonth = moment(`${year}-${month}`, "YYYY-MM").daysInMonth();
  const { userDetails, decodeToken, userRole } = CustomerDatabaseStore();
  const { deleteTourProgram, addSmartTourProgram, addTourProgramForm } =
    MonthlyTourProgramStore();

  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [selectedDeleteId, setSelectedDeleteId] = useState<number | null>(null);

  // Smart Fill state
  const [isSmartFillOpen, setIsSmartFillOpen] = useState<boolean>(false);
  const [smartFillChoice, setSmartFillChoice] = useState<"Yes" | "No" | null>(
    null
  );
  // inside component
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string }>({
    open: false,
    message: "",
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [smartTourData, setSmartTourData] = useState<SmartFillSuggestions>({});
  const [isSmartTourReportingEditable, setIsSmartTourReportingEditable] =
    useState<boolean>(true);
  const [smartholidayData, setSmartHolidayData] = useState<
    MonthlySmartFillHolidayData[]
  >([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showSmartFillRow, setShowSmartFillRow] = useState(true);
  const [selectedDate, setSelectedDate] = useState<string>("");
  const [existingToursForDate, setExistingToursForDate] = useState<
    MonthlyTourProgramData[]
  >([]);

  useEffect(() => {
    decodeToken();
  }, []);

  const fetchMonthlySmartFillData = async (): Promise<
    Record<
      string,
      {
        type: string;
        hq_brick_id: number;
        working_with_id: number;
        hq_territory_id: number;
        hq_headquarter_id: number;
      }[]
    >
  > => {
    setLoading(true);
    try {
      const payload = { month, year };
      const response = await addSmartTourProgram(payload);
      const suggestionsDateWiseData: Record<
        string,
        {
          type: string;
          hq_brick_id: number;
          working_with_id: number;
          hq_territory_id: number;
          hq_headquarter_id: number;
        }[]
      > = response?.suggestions?.suggestions || {};

      setSmartTourData(suggestionsDateWiseData);

      // ✅ Return value instead of void
      return suggestionsDateWiseData;
    } catch (error) {
      console.error("Error fetching Smart Fill data:", error);
      return {}; // ✅ Always return something
    } finally {
      setLoading(false);
    }
  };

  const handleSmartFillChoice = async (choice: "Yes" | "No") => {
    setSmartFillChoice(choice);

    if (choice === "Yes") {
      try {
        setIsSmartFillOpen(false);

        // ✅ Now returns proper type
        const res = await fetchMonthlySmartFillData();

        if (!res || Object.keys(res).length === 0) {
          await Swal.fire({
            title: "No Smart Fill Data",
            text: "No recommended tours found for this month.",
            icon: "info",
            confirmButtonColor: "#1976d2",
          });
          return;
        }

        // ✅ Flatten data
        const flatData = Object.entries(res).flatMap(([date, entries]) =>
          (entries || []).map((entry) => ({
            date,
            type: entry.type,
            hq_brick_id: entry.hq_brick_id,
            working_with_id: entry.working_with_id,
            hq_territory_id: entry.hq_territory_id,
            hq_headquarter_id: entry.hq_headquarter_id,
          }))
        );

        const payload = {
          tours: flatData.map((row) => ({
            date: row.date,
            hq_brick_id: row.hq_brick_id,
            type: row.type,
            user_id: userDetails?.id,
            working_with_id: row.working_with_id,
          })),
        };

        const apiResponse = await addTourProgramForm(payload);

        if (apiResponse?.success) {
          const createdCount = apiResponse.created || 0;

          let message = `✅ ${createdCount} tour${
            createdCount > 1 ? "s" : ""
          } created successfully.`;

          if (apiResponse.errors?.length) {
            const errorList = apiResponse.errors
              .map(
                (err: any) =>
                  `📅 ${moment(err.date).format("D MMM YYYY")} - ${err.message}`
              )
              .join("\n");
            message += `\n⚠️ Some issues occurred:\n${errorList}`;
          }

          await Swal.fire({
            title: "Smart Fill Result",
            text: message,
            icon: apiResponse.errors?.length ? "warning" : "success",
            confirmButtonColor: "#1976d2",
          });

          await refreshData?.();
        } else {
          await Swal.fire({
            title: "Failed",
            text: apiResponse?.message || "Smart Fill update failed.",
            icon: "error",
            confirmButtonColor: "#d33",
          });
        }
      } catch (err) {
        console.error("Error in Smart Fill:", err);
        await Swal.fire({
          title: "Error",
          text: "Something went wrong during Smart Fill submission.",
          icon: "error",
          confirmButtonColor: "#d33",
        });
      } finally {
        setShowSmartFillRow(false);
        setSmartTourData({});
        setSmartFillChoice(null);
      }
    } else {
      // if No
      setShowSmartFillRow(false);
      setIsSmartFillOpen(false);
      setSmartTourData({});
    }
  };

  // When closing the modal, reset the choice if needed
  const handleSmartFillClose = () => {
    setIsSmartFillOpen(false);
    setSmartFillChoice(null); // reset selection
  };

  const fullMonthData = Array.from({ length: daysInMonth }, (_, i) => {
    const date = moment(`${year}-${month}-${i + 1}`, "YYYY-MM-DD").format(
      "YYYY-MM-DD"
    );
    const isSunday = moment(date).day() === 0;
    const apiRow = data.filter((d) => d.date === date); // all entries for this date
    const holidayEntry = holiday.find((h) => h.date === date);

    return {
      date,
      isSunday,
      rowData: apiRow, // array now
      holiday: holidayEntry || null,
    };
  });

  const groupedByWeek: Record<string, typeof fullMonthData> = {};
  fullMonthData.forEach((item) => {
    const weekNum =
      moment(item.date).week() - moment(`${year}-${month}-01`).week() + 1;
    const weekKey = `Week ${weekNum}`;
    if (!groupedByWeek[weekKey]) groupedByWeek[weekKey] = [];
    groupedByWeek[weekKey].push(item);
  });

  const weekKeys = Object.keys(groupedByWeek);

  const renderStatusChip = (status: string | null) => {
    const commonStyle = {
      color: "#212121",
      fontWeight: 500,
      height: 20,
      fontSize: "0.7rem",
      borderRadius: "10px",
      px: 0.75,
    };
    switch (status) {
      case "active":
        return (
          <Chip
            label="Approved"
            size="small"
            sx={{ backgroundColor: "#6FC276", ...commonStyle }}
          />
        );
      case "inactive":
        return (
          <Chip
            label="Rejected"
            size="small"
            sx={{ backgroundColor: "#F08080", ...commonStyle }}
          />
        );
      case "pending":
        return (
          <Chip
            label="Approval Pending"
            size="small"
            sx={{ backgroundColor: "#FFE37A", ...commonStyle }}
          />
        );
      case null:
        return (
          <Chip
            label="Tour Not Set"
            size="small"
            sx={{ backgroundColor: "#FF964F", ...commonStyle }}
          />
        );
      default:
        return <Chip label={status} size="small" sx={commonStyle} />;
    }
  };

  const meetingDates = data
    .filter((d) => d.type === "Meeting" && d.status === "active")
    .map((d) => d.date);
  const renderActions = (
    rowData: MonthlyTourProgramData | null,
    isHoliday: boolean,
    date: string
  ) => {
    if (isHoliday) return "-";

    const isSpecialAccess = specialAccessDays.includes(
      moment(date).format("YYYY-MM-DD")
    );
    const isActionDisabled = !editable && !isSpecialAccess;

    // 🟡 Find all entries for this date
    const dateEntries = data.filter((d) => d.date === date);

    // 🟢 Allow multiple only for Field Work
    const hasNonFieldWork = dateEntries.some((d) => d.type !== "Field Work");
    const disableAdd =
      isActionDisabled || (hasNonFieldWork && rowData?.type !== "Field Work");

    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        gap={1}
        minWidth={48}
      >
        <IconButton
          size="small"
          sx={{
            color: disableAdd ? "grey.400" : "#1976d2",
            "&:hover": { color: disableAdd ? "grey.400" : "#115293" },
          }}
          onClick={() => handleAddClick(date, rowData)}
          disabled={disableAdd}
        >
          <AddCircleOutline fontSize="small" />
        </IconButton>

        {rowData?.id ? (
          <IconButton
            size="small"
            color="error"
            onClick={() => handleDeleteClick(rowData.id)}
            disabled={isActionDisabled}
          >
            <Delete fontSize="small" />
          </IconButton>
        ) : (
          <Box width={24} />
        )}
      </Box>
    );
  };

  const handleAddClick = (
    date: string,
    rowData: MonthlyTourProgramData | null
  ) => {
    const dateEntries = data.filter((d) => d.date === date);

    // ❌ If non-Field Work already exists for this date, block adding
    const hasNonFieldWork = dateEntries.some((d) => d.type !== "Field Work");

    if (hasNonFieldWork && (!rowData || rowData.type !== "Field Work")) {
      setSnackbar({
        open: true,
        message: "Only Field Work allows multiple entries for the same date.",
      });
      return;
    }

    if (rowData?.type === "Meeting") {
      setSnackbar({
        open: true,
        message: "Meeting already added for this day. Cannot add Field Work.",
      });
      return;
    }

    if (rowData?.type === "Meeting" && rowData.status === "active") {
      setSnackbar({
        open: true,
        message: "Approved Meeting cannot be modified.",
      });
      return;
    }
    setExistingToursForDate(dateEntries); // ✅ store for modal
    setSelectedDate(date);
    setModalOpen(true);
  };

  const handleDeleteClick = (id: number) => {
    setSelectedDeleteId(id);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedDeleteId) return;
    if (isDeleting) return; // prevent double-click

    setIsDeleting(true);
    try {
      await deleteTourProgram(selectedDeleteId);
      setDeleteModalOpen(false);
      setSelectedDeleteId(null);
      refreshData?.(); // ✅ refresh the table after delete
    } catch (err) {
      console.error("Error deleting tour program:", err);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setDeleteModalOpen(false);
    setSelectedDeleteId(null);
  };

  useEffect(() => {
    setShowSmartFillRow(true);
    setSmartFillChoice(null);
  }, [month, year]);

  const handleSave = async (payload: any) => {
    try {
      await refreshData?.(); // ✅ refresh table after adding entry
    } catch (error) {
      console.error("Error refreshing after save:", error);
    }
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          width: "100%",
          mt: 1,
          mb: 3,
        }}
      >
        <Box sx={{ width: "100%", p: 1 }}>
          {/* Smart Fill row at top */}
          {editable &&
            showSmartFillRow &&
            weekKeys.length > 0 &&
            data.length <= 5 && (
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                sx={{
                  backgroundColor: "#fff",
                  p: 1.5,
                  mb: 2,
                  borderRadius: 2,
                  boxShadow: 1,
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  <img
                    src={IMAGES.SmartFill}
                    alt="Smart Fill"
                    style={{ width: 24, height: 24, objectFit: "contain" }}
                  />
                  <Typography
                    variant="subtitle2"
                    fontWeight={550}
                    sx={{ color: "#226E9E" }}
                  >
                    Would you like to use smart fill?
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1}>
                  {["Yes", "No"].map((choice) => (
                    <Button
                      key={choice}
                      variant={
                        smartFillChoice === choice ? "contained" : "outlined"
                      }
                      onClick={() =>
                        handleSmartFillChoice(choice as "Yes" | "No")
                      }
                      sx={{
                        color: "#226E9E",
                        borderRadius: "16px",
                        textTransform: "none",
                        minWidth: "50px",
                        paddingY: 0.1,
                        paddingX: 1.2,
                      }}
                    >
                      {choice}
                    </Button>
                  ))}
                </Stack>
              </Box>
            )}

          {/* Weeks and Cards */}
          {weekKeys.map((week) => {
            const weekData = groupedByWeek[week];
            const filteredWeekData = weekData
              .map((item) => {
                const rows = item.rowData.filter((row) => {
                  if (!row)
                    return (
                      filterType === "All" || filterType === "Tour Not Set"
                    );
                  switch (filterType) {
                    case "All":
                      return true;
                    case "Approved":
                      return row.status === "active";
                    case "Rejected":
                      return row.status === "inactive";
                    case "Approval Pending":
                      return row.status === "pending";
                    case "Tour Not Set":
                      return row.status === null;
                    default:
                      return true;
                  }
                });
                return rows.length > 0 ||
                  filterType === "All" ||
                  filterType === "Tour Not Set"
                  ? { ...item, rowData: rows.length > 0 ? rows : [null] }
                  : null;
              })
              .filter((x): x is (typeof weekData)[number] => Boolean(x));

            if (filteredWeekData.length === 0) return null;

            return (
              <Box key={week} sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  sx={{ mb: 1, color: "#226E9E" }}
                >
                  {week}
                </Typography>

                {filteredWeekData.map((item) =>
                  item.rowData.map((row, idx) => (
                    <Card
                      key={`${item.date}-${idx}`}
                      sx={{
                        borderRadius: 2,
                        mb: 1.5,
                        boxShadow: 2,
                        bgcolor: "#fff",
                        p: 1.2,
                      }}
                    >
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{ mb: 1 }}
                      >
                        <Typography
                          variant="subtitle1"
                          fontWeight="600"
                          color="#226E9E"
                        >
                          {row?.type || "Tour Not Set"}
                        </Typography>
                        {renderStatusChip(row?.status || null)}
                      </Stack>
                      {row?.type === "Field Work" ? (
                        <>
                          {/* Working With */}
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {row?.working_with?.full_name || "-"}
                          </Typography>

                          {/* Brick | Territory */}
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 1 }}
                          >
                            {row?.hq_brick?.name || row?.hq_territory?.name
                              ? [row?.hq_brick?.name, row?.hq_territory?.name]
                                  .filter(Boolean)
                                  .join(" | ")
                              : "-"}
                          </Typography>
                        </>
                      ) : (
                        <Box sx={{ mb: 1 }} /> // Keep spacing consistent
                      )}

                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Typography variant="body2" color="text.secondary">
                          {moment(item.date).format("Do MMM")}
                        </Typography>
                        <Stack direction="row" spacing={1}>
                          {(() => {
                            const isSpecialAccess = specialAccessDays.includes(
                              moment(item.date).format("YYYY-MM-DD")
                            );

                            // Allow Add if editable OR this date has special access
                            const canAdd = editable || isSpecialAccess;
                            const canDelete = editable || isSpecialAccess;

                            return (
                              <>
                                <IconButton
                                  size="small"
                                  sx={{
                                    color: canAdd ? "#1976d2" : "grey.400",
                                    "&:hover": {
                                      color: canAdd ? "#115293" : "grey.400",
                                    },
                                  }}
                                  onClick={() =>
                                    handleAddClick(item.date, row || null)
                                  }
                                  disabled={!canAdd}
                                >
                                  <AddCircleOutline fontSize="small" />
                                </IconButton>

                                {row?.id && (
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleDeleteClick(row.id)}
                                    disabled={!canDelete}
                                  >
                                    <Delete fontSize="small" />
                                  </IconButton>
                                )}
                              </>
                            );
                          })()}
                        </Stack>
                      </Stack>
                    </Card>
                  ))
                )}
              </Box>
            );
          })}

          {data.length === 0 && (
            <Typography mt={2} textAlign="center" color="text.secondary">
              No records found for this filter.
            </Typography>
          )}
        </Box>
      </Box>

      {/* Delete Confirmation Modal */}
      <ConfirmDeleteModalMobile
        open={deleteModalOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        isDeleting={isDeleting} // ✅ pass down
      />

      <AddTourProgramFormModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={handleSave}
        tourTypes={tourType}
        defaultDate={selectedDate}
        meetingDates={meetingDates} // ✅ new prop
        existingTours={existingToursForDate} // ✅ new prop
      />
      {loading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            bgcolor: "rgba(255, 255, 255, 0.6)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 2000,
          }}
        >
          <CircularProgress size={60} />
        </Box>
      )}

      <SmartFillModalMobile
        open={isSmartFillOpen}
        onClose={handleSmartFillClose}
        data={smartTourData}
        loading={loading} // replace with actual loading state
        refreshData={refreshData} // ✅ pass refresh function
        closeParentModal={() => setIsSmartFillOpen(false)} // ✅ optional helper
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ open: false, message: "" })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity="warning"
          sx={{
            width: "100%",
            backgroundColor: "#b64a4aff",
            color: "#ffffffff",
            "& .MuiAlert-icon": {
              color: "#ffffff", // 👈 Change icon color here
            },
          }}
          onClose={() => setSnackbar({ open: false, message: "" })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default TourProgramCardViewMobile;
